import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setPdpaConsent } from '@/stores/appSlice';
import { selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { PdpaNoticeBody } from './pdpaNoticeContent';

export default function Pdpa() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const [consentWarning, setConsentWarning] = useState<string | null>(null);

  useEffect(() => {
    if (pdpaConsent) setConsentWarning(null);
  }, [pdpaConsent]);

  const handleAgree = () => {
    if (!pdpaConsent) {
      setConsentWarning(t.pdpa.consentWarning);
      return;
    }
    navigate('/uniquecode');
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden pt-[10%] pb-8">
        <SurveyBrownPanelInset className="mt-4 flex min-h-0 flex-1 flex-col">
          <SurveyBrownPanel className="flex min-h-0 flex-1 flex-col overflow-hidden !p-0 max-h-[70dvh]">
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
              <h1 className="text-center text-[36px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] sm:text-[42px]">
                {t.pdpa.title}
              </h1>
              <h2 className="mt-4 text-center text-[14px] font-semibold leading-snug tracking-wide text-white/95 sm:text-[15px]">
                {t.pdpa.noticeTitle}
              </h2>
              <PdpaNoticeBody />
            </div>
          </SurveyBrownPanel>
        </SurveyBrownPanelInset>
        <SurveyBrownPanelInset className="mt-4 shrink-0">
          <label
            htmlFor="pdpa-consent"
            className="flex cursor-pointer items-start gap-3 text-left text-[13px] leading-snug text-white/95 sm:text-[14px]"
          >
            <input
              id="pdpa-consent"
              type="checkbox"
              checked={pdpaConsent}
              onChange={(e) => dispatch(setPdpaConsent(e.target.checked))}
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-white/50 bg-black/20 accent-[#c9a882] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
            />
            <span>{t.pdpa.consentText}</span>
          </label>
        </SurveyBrownPanelInset>
        <SurveyBrownPanelInset className="mt-6 shrink-0">
          <div className="flex flex-col items-center gap-3">
            {consentWarning != null ? (
              <SurveyValidationAlert>{consentWarning}</SurveyValidationAlert>
            ) : null}
            <div className="flex w-full flex-nowrap items-center justify-evenly gap-3">
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                onClick={handleAgree}
              >
                {t.pdpa.agree}
              </MetallicPillButton>
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                onClick={() => navigate('/thankyou')}
              >
                {t.pdpa.decline}
              </MetallicPillButton>
            </div>
          </div>
        </SurveyBrownPanelInset>
      </div>
    </SurveyBackgroundLayout>
  );
}
